import { Box, Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getLocalValue,
  LoacalVariables,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import http from "../../../commonFunctions/httpClient";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import WSButton from "../../../Components/Button/WSButton";
import WSSelectField from "../../../Components/Select/WSSelect";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import WSTextField, {
  CurrencyType,
  TextType,
} from "../../../Components/TextBox/WSTextBox";
import WSBackButton from "../../../Components/BackButton/BackButton";

interface LabScanFormData {
  labId: string;
  scanSubcategoryId: string;
  mrpPrice: number;
  discountPercentage: number;
  turnaroundTime: string;
}

const LabScanCreate: React.FC<any> = React.memo((props) => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [scanSubcategoryOptions, setScanSubcategoryOptions] = useState<any[]>([]);
  const [labId, setLabId] = useState<string>("");
  const id: any = params?.id;
  const isEditMode = !!id;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<LabScanFormData>({
    mode: "onChange",
  });

  useEffect(() => {
    updateMainHeader(isEditMode ? "Update Scan Service" : "Add Scan Service");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Services": "/scan-test/list" },
      { [isEditMode ? "Update Scan" : "Add Scan"]: "/" },  // ✅ FIXED: Proper syntax
    ]);
  }, [isEditMode]);

  // Get labId from local storage
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
    setValue("labId", userId || "");
  }, [setValue]);

  // Fetch scan subcategories
  // ✅ JUST REPLACE THIS ONE FUNCTION - Everything else is perfect!
const fetchScanSubcategories = async () => {
  try {
    const response: any = await http.get("pharmacy/ScanSubCategory");
    const categories = response?.categories || [];
    
    const options = categories.map((item: any) => ({
      value: item.id,
      label: item.name  // ✅ "Cardiac-MRI", "UV-C Scan", etc.
    }));
    
    setScanSubcategoryOptions(options);
    console.log("✅ LOADED", options.length, "scan categories");
    
  } catch (error) {
    console.error("Error:", error);
    setScanSubcategoryOptions([]);
  }
};



  useEffect(() => {
    fetchScanSubcategories();
  }, []);

  // Load existing scan service for edit
  useEffect(() => {
    async function getLabScanById(scanId: any): Promise<void> {
      if (!scanId || !labId) return;

      try {
        const response: any = await http.get(`lab/Labscanning/${scanId}`);
        const scanData = response?.data || response;
        console.log("Scan data response:", scanData);

        if (scanData) {
          setValue("labId", scanData.labId || labId);
          setValue("scanSubcategoryId", scanData.scanSubcategoryId || "");
          setValue("mrpPrice", scanData.mrpPrice?.toString() || "0");
          setValue("discountPercentage", scanData.discountPercentage?.toString() || "0");
          setValue("turnaroundTime", scanData.turnaroundTime || "");
        }
      } catch (error: any) {
        console.error("Error fetching scan service:", error);
        showMessage("Error fetching scan service", "error");
      }
    }

    if (id && labId) {
      getLabScanById(id);
    }
  }, [id, labId, setValue, showMessage]);

  const onSubmit = async (data: LabScanFormData) => {
    console.log("✅ onSubmit called with data:", data);
    try {
      setLoading(true);

      const payload = {
        labId: labId || data.labId,
        scanSubcategoryId: data.scanSubcategoryId,
        mrpPrice: parseFloat(data.mrpPrice.toString()) || 0,
        discountPercentage: parseFloat(data.discountPercentage.toString()) || 0,
        turnaroundTime: data.turnaroundTime || "",
      };

      console.log("📤 API Payload:", payload);

      let response: any;
      if (isEditMode && id) {
        // Update existing scan service
        response = await http.put(`lab/Labscanning/${id}`, payload);
      } else {
        // Create new scan service
        response = await http.post("lab/Labscanning", payload);
      }

      console.log("✅ API RESPONSE:", response);

      const success =
        response?.success === true ||
        !!response?.data ||
        response?.message?.toLowerCase?.().includes("success") ||
        !response?.errors;

      if (success) {
        showMessage(
          response?.message ||
            (isEditMode ? "Scan service updated successfully!" : "Scan service created successfully!"),
          "success"
        );
        navigate("/scan-test/list");
      } else {
        showMessage(response?.message || "Failed to save scan service", "error");
      }
    } catch (e: any) {
      console.error("💥 SUBMIT ERROR:", e);

      if (e.response?.status === 422) {
        const errors = e.response.data?.errors;
        if (errors) {
          const errorList = Object.entries(errors)
            .map(([field, msgs]: any) => `${field}: ${Array.isArray(msgs) ? msgs[0] : msgs}`)
            .join("\n");
          showMessage(`Validation Errors:\n${errorList}`, "error");
        }
      } else {
        showMessage(
          e.response?.data?.message || e.response?.data?.title || e.message || "Failed to save scan service",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/scan-test/list");
  };

  const buttonLabel = isEditMode ? "Update" : "Save";

  return (
    <>
      <WSBackButton id="btnScanBack" url={"/scan-test/list"} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              style={{
                paddingLeft: 20,
                paddingRight: 20,
                marginTop: 0,
                paddingBottom: 25,
                borderRadius: "8px",
              }}
            >
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                    Scan Service Information
                  </Typography>
                </Grid>

                {/* ✅ REMOVED: Invalid hidden input - labId handled via setValue */}

                <Grid item sm={6} xs={12}>
                  <WSSelectField
                    rules={{ required: true }}
                    control={control}
                    name="scanSubcategoryId"
                    id="scanSubcategoryId"
                    label="Scan Subcategory *"
                    options={scanSubcategoryOptions}
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}
                    id="mrpPrice"
                    name="mrpPrice"
                    rules={{ required: true }}
                    label="MRP Price *"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    type={TextType.Number}
                    id="discountPercentage"
                    name="discountPercentage"
                    rules={{ required: true }}
                    label="Discount Percentage *"
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="turnaroundTime"
                    name="turnaroundTime"
                    rules={{ required: true }}
                    label="Turnaround Time *"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
            <Grid item>
              <WSButton
                id="cancel"
                type="button"
                label="Cancel"
                size="medium"
                onClick={handleCancelClick}
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  border: "1px solid #098963",
                  color: "#098963",
                  backgroundColor: "#FFFFFF",
                }}
              />
            </Grid>
            <Grid item>
              <WSButton
                id="submit"
                type="submit"
                label={buttonLabel}
                size="medium"
                variant="outlined"
                color="primary"
                loading={loading}
                disabled={!isValid || loading}
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  border: "1px solid #098963",
                  color: "#FFFFFF",
                  backgroundColor: "#098963",
                  "&:hover": {
                    backgroundColor: "#066e4f",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
});

export default LabScanCreate;
